import { HeadersBuilder } from './headers/headers-builder';
import { ParameterType } from './parameter-type.enum';
import { LoaderService } from './../loader.service';
import { TriggerbeeUrlBuilder } from './triggerbee-url-builder.service';
import { Logger, LoggerFactory } from './../logger.service';
import { RequestResult } from './request-result';

import { TriggerbeeApi } from './triggerbee-api.enum';
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { from, Observable } from 'rxjs';

export { TriggerbeeApi } from './triggerbee-api.enum';
export { TriggerbeeUrlBuilder } from './triggerbee-url-builder.service';
export { ParameterType } from './parameter-type.enum';

class ApiResponseContainer<T> {
  public body: any;
  public error: string;
  public status: number;
}

@Injectable()
export class TriggerbeeRequester {
  // Logs operation (GET, POST, ...) with parameters/headers, and logs even the response

  protected logger: Logger;
  constructor(
    protected readonly http: HttpClient,
    public readonly loggerFactory: LoggerFactory,
    protected readonly urlBuilder: TriggerbeeUrlBuilder,
    protected readonly loader: LoaderService,
  ) {
    this.logger = loggerFactory.get('TriggerbeeRequester');
  }

  // ---------------------------------------------
  // ---------------- Observable -----------------
  // ---------------------------------------------
  public getObservable<T>(
    api: TriggerbeeApi,
    resource = '',
    parameters: any = undefined,
  ): Observable<RequestResult<any>> {
    return from(this.getAsync(api, resource, parameters));
  }
  public postObservable<T>(
    api: TriggerbeeApi,
    resource = '',
    body: any = undefined,
    format: ParameterType = ParameterType.Json,
  ): Observable<RequestResult<any>> {
    return from(this.postAsync(api, resource, body, format));
  }

  // ---------------------------------------------
  // ------------------ Promise ------------------
  // ---------------------------------------------
  public postAsync<T>(
    api: TriggerbeeApi,
    resource = '',
    parameters: any = undefined,
    format: ParameterType = ParameterType.Json,
  ): Promise<RequestResult<T>> {
    // Build url
    const url = this.urlBuilder.buildUrl(api, resource);
    if (!url) {
      return this.rejectAsync<T>('Unknown triggerbee url.', '');
    }
    return this.postInternalAsync<T>(url, parameters, format);
  }
  public putAsync<T>(
    api: TriggerbeeApi,
    resource = '',
    parameters: any = undefined,
    format: ParameterType = ParameterType.Json,
  ): Promise<RequestResult<T>> {
    // Build url
    const url = this.urlBuilder.buildUrl(api, resource);
    if (!url) {
      return this.rejectAsync<T>('Unknown triggerbee url.', '');
    }
    return this.putInternalAsync<T>(url, parameters, format);
  }
  public deleteAsync<T>(
    api: TriggerbeeApi,
    resource = '',
    parameters: any = undefined,
    format: ParameterType = ParameterType.Json,
  ): Promise<RequestResult<T>> {
    // Build url
    const url = this.urlBuilder.buildUrl(api, resource);
    if (!url) {
      return this.rejectAsync<T>('Unknown triggerbee url.', '');
    }
    return this.deleteInternalAsync<T>(url, parameters, format);
  }
  public getAsync<T>(api: TriggerbeeApi, resource = '', parameters: any = undefined): Promise<RequestResult<T>> {
    // Build url
    const url = this.urlBuilder.buildUrl(api, resource, parameters);
    if (!url) {
      return this.rejectAsync<T>('Unknown triggerbee url.', '');
    }
    // Return from url
    return this.getInternalAsync<T>(url);
  }

  public async directGetAsync(
    api: TriggerbeeApi,
    resource = '',
    parameters: any = undefined,
  ): Promise<HttpResponse<any>> {
    const url = this.urlBuilder.buildUrl(api, resource, parameters);
    const headers = new HeadersBuilder();
    // Send request
    return await this.http
      .get(url, {
        headers: headers.get(),
        observe: 'response' /* needed to get status */,
        withCredentials: true /* needed to set cookies */,
      })
      .toPromise();
  }

  // ---------------------------------------------
  // ------------- Angular.Http ------------------
  // ---------------------------------------------
  private async getInternalAsync<T>(url: string): Promise<RequestResult<T>> {
    this.loader.setBusy();
    // Prepare options
    const headers = new HeadersBuilder();

    // Send request
    const result = await this.http
      .get(url, {
        headers: headers.get(),
        observe: 'response' /* needed to get status */,
        withCredentials: true /* needed to set cookies */,
      })
      .toPromise()
      .then(async (response) => this.handleResponseAsync<T>(JSON.stringify(await response), response.status, url))
      .catch(async (response) => this.handleResponseAsync<T>(JSON.stringify(await response), response.status, url));
    this.loader.removeBusy();
    return result;
  }

  private async postInternalAsync<T>(url: string, body: any, parameterType: ParameterType): Promise<RequestResult<T>> {
    this.loader.setBusy();
    // Prepare options
    const headers = new HeadersBuilder().addContentType(parameterType);
    const serialized: string = this.getPostBody(body, parameterType);

    // Send request
    const result = await this.http
      .post<T>(url, serialized, {
        headers: headers.get(),
        observe: 'response' /* needed to get status */,
        withCredentials: true /* needed to set cookies */,
      })
      .toPromise()
      .then(async (response) => this.handleResponseAsync<T>(JSON.stringify(await response), response.status, url))
      .catch(async (response) => this.handleResponseAsync<T>(JSON.stringify(await response), response.status, url));
    this.loader.removeBusy();
    return result;
  }

  private putInternalAsync<T>(url: string, body: any, parameterType: ParameterType): Promise<RequestResult<T>> {
    // Prepare options
    const headers = new HeadersBuilder().addContentType(parameterType);
    const serialized: string = this.getPostBody(body, parameterType);

    // Send request
    return this.http
      .put(url, serialized, {
        headers: headers.get(),
        observe: 'response' /* needed to get status */,
        withCredentials: true /* needed to set cookies */,
      })
      .toPromise()
      .then(async (response) => this.handleResponseAsync<T>(JSON.stringify(await response), response.status, url))
      .catch(async (response) => this.handleResponseAsync<T>(JSON.stringify(await response), response.status, url));
  }

  private deleteInternalAsync<T>(url: string, body: any, parameterType: ParameterType): Promise<RequestResult<T>> {
    // Prepare options
    const headers = new HeadersBuilder().addContentType(parameterType);
    const serialized: string = this.getPostBody(body, parameterType);

    // Send request
    return this.http
      .request('DELETE', url, {
        headers: headers.get(),
        body: serialized,
        observe: 'response' /* needed to get status */,
        withCredentials: true /* needed to set cookies */,
      })
      .toPromise()
      .then(async (response) => this.handleResponseAsync<T>(JSON.stringify(await response), response.status, url))
      .catch(async (response) => this.handleResponseAsync<T>(JSON.stringify(await response), response.status, url));
  }

  // ---------------------------------------------
  // ---------------- Helpers --------------------
  // ---------------------------------------------
  private handleResponseAsync<T>(
    jsonString: string,
    statusCode: number,
    requestUrl: string,
  ): Promise<RequestResult<T>> {
    this.handleUnauthorizedOrForbidden(statusCode, requestUrl);
    return this.buildResponseAsync(jsonString, statusCode, requestUrl);
  }

  private async buildResponseAsync<T>(
    jsonString: string,
    statusCode: number,
    requestUrl: string,
  ): Promise<RequestResult<T>> {
    if (!jsonString) {
      return this.rejectAsync<T>('JSON string from response is undefined', requestUrl);
    }
    const apiResponseContainer = JSON.parse(jsonString) as ApiResponseContainer<T>;
    const apiResponse = apiResponseContainer.body || apiResponseContainer.error;
    if (!apiResponse) {
      if (statusCode >= 200 && statusCode <= 299) {
        return new RequestResult<T>(undefined, undefined, statusCode);
      }
      this.logger.error(`Could not convert it into ApiResponse.
                               Failed endpoint : ${requestUrl}.
                               Ensure all API's return always ApiResponse format.`);
      return new RequestResult<T>(undefined, undefined, statusCode);
    }
    let result = new RequestResult<T>(apiResponse?.error, apiResponse?.data, statusCode);
    if (apiResponse?.data === undefined) {
      result = new RequestResult<T>(apiResponseContainer.error, apiResponseContainer.body, apiResponseContainer.status);
    }
    return result;
  }

  private getPostBody(body: any, parameterType: ParameterType): string {
    switch (parameterType) {
      case ParameterType.Json:
        return JSON.stringify(body);
      case ParameterType.FormUrlEncoded:
        return this.urlEncode(body);
      default:
        this.logger.error('Unknown parameter type');
    }
  }

  private urlEncode(object: any) {
    const urlSearchParams = new URLSearchParams();
    Object.keys(object).map((key) => urlSearchParams.append(key, object[key]));
    return urlSearchParams.toString();
  }

  private handleUnauthorizedOrForbidden(statusCode: number, requestUrl: string) {
    if (!(statusCode === 401 /*Unauthorized*/ || statusCode === 403 /* Forbidden */)) {
      // No need to handle
      return;
    }
    this.logger.warn(`Unauthorized access to ${requestUrl}`);
  }

  private rejectAsync<T>(error: string, url: string): Promise<RequestResult<T>> {
    this.logger.error(`An error occured while fetching from ${url}\n Error :${error}`);
    // Release the loader on rejection to prevent it from getting stuck.
    this.loader.removeBusy();
    return Promise.reject(error);
  }
}
